import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { inboundProjectsAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  FiArrowLeft, 
  FiEdit, 
  FiSend, 
  FiX, 
  FiAlertCircle, 
  FiPackage,
  FiTruck,
  FiCalendar,
  FiUser,
  FiMapPin,
  FiFileText,
  FiActivity
} from 'react-icons/fi';

const getReceivingServiceLabels = (instructions = {}) => {
  const labels = [];
  if (instructions.require_serial_numbers_on_receive) labels.push('Record serial numbers on receipt');
  if (instructions.require_mac_addresses_on_receive) labels.push('Record MAC addresses on receipt');
  if (instructions.relabel_on_receive) labels.push('Relabel units on receipt');
  return labels;
};

const InboundProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Fetch project details
  const { 
    data: projectData, 
    isLoading, 
    error 
  } = useQuery(
    ['inboundProject', id], 
    () => inboundProjectsAPI.getProject(id),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );

  // Fetch project lines
  const { 
    data: linesData 
  } = useQuery(
    ['inboundProjectLines', id], 
    () => inboundProjectsAPI.getProjectLines(id),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );

  // Fetch project summary
  const { 
    data: summaryData 
  } = useQuery(
    ['inboundProjectSummary', id], 
    () => inboundProjectsAPI.getProjectSummary(id),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );

  const project = projectData?.project;
  const lines = linesData?.lines || [];
  const summary = summaryData?.summary || {};
  const receivingServiceLabels = getReceivingServiceLabels(
    project?.receivingInstructions || project?.receiving_instructions || {}
  );

  useEffect(() => {
    if (project?.Request?.id) {
      navigate(`/customer/requests/${project.Request.id}`, { replace: true });
    }
  }, [navigate, project?.Request?.id]);

  // Submit project mutation
  const submitProjectMutation = useMutation(
    () => inboundProjectsAPI.submitProject(id),
    {
      onSuccess: () => {
        showNotification('Project submitted successfully!', 'success');
        queryClient.invalidateQueries(['inboundProject', id]);
        queryClient.invalidateQueries(['inboundProjectSummary', id]);
      },
      onError: (error) => {
        showNotification(
          error.response?.data?.message || 'Failed to submit project',
          'error'
        );
      }
    }
  );

  // Cancel project mutation
  const cancelProjectMutation = useMutation(
    (data) => inboundProjectsAPI.cancelProject(id, data),
    {
      onSuccess: () => {
        showNotification('Project cancelled successfully', 'success');
        queryClient.invalidateQueries(['inboundProject', id]);
        queryClient.invalidateQueries(['inboundProjectSummary', id]);
        setShowCancelDialog(false);
        setCancelReason('');
      },
      onError: (error) => {
        showNotification(
          error.response?.data?.message || 'Failed to cancel project',
          'error'
        );
      }
    }
  );

  // Handle submit project
  const handleSubmitProject = () => {
    submitProjectMutation.mutate();
  };

  // Handle cancel project
  const handleCancelProject = () => {
    cancelProjectMutation.mutate({ reason: cancelReason });
  };

  // Status color mapping
  const getStatusColor = (status) => {
    const statusColors = {
      'draft': 'bg-gray-100 text-gray-800',
      'uploaded': 'bg-blue-100 text-blue-800',
      'mapped': 'bg-yellow-100 text-yellow-800',
      'validated': 'bg-green-100 text-green-800',
      'submitted': 'bg-purple-100 text-purple-800',
      'approved': 'bg-indigo-100 text-indigo-800',
      'partially_received': 'bg-orange-100 text-orange-800',
      'received': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <FiAlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <h3 className="text-lg font-medium text-red-800">Error Loading Project</h3>
        </div>
        <p className="text-red-700 mt-2">
          {error?.message || 'Project not found'}
        </p>
        <button
          onClick={() => navigate('/customer/inbound-projects')}
          className="mt-4 text-red-600 hover:text-red-800 underline"
        >
          Return to Projects List
        </button>
      </div>
    );
  }

  if (project?.Request?.id) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const canEdit = ['draft', 'uploaded'].includes(project.status);
  const canSubmit = ['mapped', 'validated'].includes(project.status);
  const canCancel = !['received', 'cancelled'].includes(project.status);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/customer/inbound-projects')}
            className="text-gray-600 hover:text-gray-800 p-2"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {project.name || 'Unnamed Manifest'}
            </h1>
            <p className="text-gray-600">
              {project.project_code && `Code: ${project.project_code}`}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(project.status)}`}>
            {project.status?.replace('_', ' ').toUpperCase()}
          </span>
          
          {canEdit && (
            <Link
              to={`/customer/inbound-projects/${id}/edit`}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center space-x-2 transition-colors"
            >
              <FiEdit className="w-4 h-4" />
              <span>Edit</span>
            </Link>
          )}

          {canSubmit && (
            <button
              onClick={handleSubmitProject}
              disabled={submitProjectMutation.isLoading}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md flex items-center space-x-2 transition-colors"
            >
              <FiSend className="w-4 h-4" />
              <span>Submit</span>
            </button>
          )}

          {canCancel && (
            <button
              onClick={() => setShowCancelDialog(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center space-x-2 transition-colors"
            >
              <FiX className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          )}
        </div>
      </div>

      {/* Project Overview */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Manifest Overview</h2>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <FiMapPin className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Destination Warehouse</p>
                  <p className="text-sm text-gray-900">
                    {project.DestinationWarehouse?.name || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <FiCalendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Expected Arrival</p>
                  <p className="text-sm text-gray-900">
                    {formatDate(project.expected_arrival)}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <FiTruck className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Vendor</p>
                  <p className="text-sm text-gray-900">
                    {project.vendor || 'Not specified'}
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <FiPackage className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Total Items</p>
                  <p className="text-sm text-gray-900">
                    {summary.lines?.total || lines.length || 0}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <FiActivity className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Progress</p>
                  <p className="text-sm text-gray-900">
                    {summary.quantities?.completionPercentage || 0}% complete
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${summary.quantities?.completionPercentage || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <FiUser className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Created By</p>
                  <p className="text-sm text-gray-900">
                    {project.CreatedBy?.name || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <FiCalendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Created</p>
                  <p className="text-sm text-gray-900">
                    {formatDate(project.created_at)}
                  </p>
                </div>
              </div>

              {project.submitted_at && (
                <div className="flex items-center space-x-2">
                  <FiSend className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Submitted</p>
                    <p className="text-sm text-gray-900">
                      {formatDate(project.submitted_at)}
                    </p>
                  </div>
                </div>
              )}

            </div>
          </div>

          {project.notes && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-start space-x-2">
                <FiFileText className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                  <p className="text-sm text-gray-900">{project.notes}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-start space-x-2">
              <FiPackage className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Receiving Instructions</p>
                {receivingServiceLabels.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {receivingServiceLabels.map((label) => (
                      <span
                        key={label}
                        className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No special receiving services requested for this manifest.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Lines */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
        </div>

        {lines.length === 0 ? (
          <div className="p-8 text-center">
            <FiPackage className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No line items found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty Expected</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty Received</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Packaging</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lines.map((line) => (
                  <tr key={line.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      #{line.line_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {line.original_sku || (
                        <span className="text-gray-400 italic">Not specified</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {line.original_description || (
                        <span className="text-gray-400 italic">Not specified</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {line.qty_expected}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {line.qty_received || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {line.packaging_hint || 'each'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cancel Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelProject}
        title="Cancel Project"
        message="Are you sure you want to cancel this inbound project? This action cannot be undone."
        confirmText="Cancel Project"
        confirmStyle="danger"
        isLoading={cancelProjectMutation.isLoading}
      >
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for cancellation (optional)
          </label>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={3}
            placeholder="Explain why you're cancelling this project..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </ConfirmDialog>
    </div>
  );
};

export default InboundProjectDetail; 
